// Admin: print-agent detail.  Edit metadata, manage printer fleet
// of this agent, rotate-token (shown once), archive/restore, hard-delete.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import {
  printAgentsAdminApi,
  type PrintAgentPrinter,
  type PrintAgentRead,
} from "@/lib/api";

const ONLINE_FRESH_SECONDS = 60;

export default function PrintAgentDetail() {
  return (
    <RequirePermission codes="print.agent.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin", "print-agents", id],
    queryFn: () => printAgentsAdminApi.get(id!),
    enabled: !!id,
    refetchInterval: 15_000,
  });

  const [rotatedToken, setRotatedToken] = useState<string | null>(null);

  const rotate = useMutation({
    mutationFn: () => printAgentsAdminApi.rotateToken(id!),
    onSuccess: (r) => {
      setRotatedToken(r.token);
      void qc.invalidateQueries({ queryKey: ["admin", "print-agents", id] });
    },
  });

  const toggleActive = useMutation({
    mutationFn: (is_active: boolean) =>
      printAgentsAdminApi.update(id!, { is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "print-agents", id] });
      void qc.invalidateQueries({ queryKey: ["admin", "print-agents"] });
    },
  });

  const del = useMutation({
    mutationFn: () => printAgentsAdminApi.remove(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "print-agents"] });
      navigate("/admin/print-agents");
    },
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState error={q.error} />;
  if (!q.data) return null;
  const a = q.data;
  const online = isOnline(a);

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPrintAgent")}
        title={a.name}
        description={t("printAgentDetail.headerDescription", { id: a.id, count: a.printers.length })}
        actions={
          <Link to="/admin/print-agents" className="btn">
            {t("printAgentDetail.toList")}
          </Link>
        }
      />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <StatusCard
          label={t("printAgentDetail.status")}
          value={!a.is_active ? t("printAgentDetail.statusOff") : online ? t("printAgentDetail.statusOnline") : t("printAgentDetail.statusOffline")}
          tone={!a.is_active ? "warn" : online ? "ok" : "warn"}
        />
        <StatusCard
          label={t("printAgentDetail.lastContact")}
          value={a.last_seen_at ? formatRelative(a.last_seen_at, t) : t("printAgentDetail.never")}
          tone="muted"
        />
        <StatusCard
          label={t("printAgentDetail.printersActive")}
          value={String(a.printers.filter((p) => p.is_active !== false).length)}
          tone="muted"
        />
      </section>

      {/* Actions */}
      <section className="card p-5 space-y-3 mb-5">
        <h3 className="text-base">{t("printAgentDetail.actions")}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="btn"
            onClick={() => rotate.mutate()}
            disabled={rotate.isPending}
          >
            {t("printAgentDetail.rotateToken")}
          </button>
          <button
            className={a.is_active ? "btn" : "btn btn-primary"}
            onClick={() => toggleActive.mutate(!a.is_active)}
            disabled={toggleActive.isPending}
          >
            {a.is_active ? t("printAgentDetail.disable") : t("printAgentDetail.enable")}
          </button>
          <button
            className="btn btn-danger"
            onClick={() => {
              if (
                confirm(
                  t("printAgentDetail.confirmDelete", { name: a.name }),
                )
              ) {
                del.mutate();
              }
            }}
            disabled={del.isPending}
          >
            {t("printAgentDetail.deleteForever")}
          </button>
        </div>
        <p className="text-text-muted text-xs">
          {t("printAgentDetail.rotateHint1")} <code className="font-mono">auth_token</code> {t("printAgentDetail.rotateHint2")}{" "}
          <code className="font-mono">agent.toml</code> {t("printAgentDetail.rotateHint3")}
        </p>
      </section>

      <PrintersEditor agent={a} />

      {rotatedToken && (
        <TokenModal token={rotatedToken} agentName={a.name} onClose={() => setRotatedToken(null)} />
      )}
    </>
  );
}

// =============================================================
// Printers editor — wholesale-replace via PUT /printers
// =============================================================

function PrintersEditor({ agent }: { agent: PrintAgentRead }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [rows, setRows] = useState<PrintAgentPrinter[]>(() => structuredClone(agent.printers));
  const [err, setErr] = useState<string | null>(null);

  // Re-hydrate when the agent's printer list changes server-side (e.g.
  // agent called /register itself and overwrote the list).
  useEffect(() => {
    setRows(structuredClone(agent.printers));
  }, [agent.printers]);

  const save = useMutation({
    mutationFn: () =>
      printAgentsAdminApi.replacePrinters(
        agent.id,
        rows
          .filter((p) => p.name.trim())
          .map((p) => ({
            name: p.name.trim(),
            // Preserve whatever the agent supplied via /register; the
            // admin UI doesn't edit this field.
            ...(p.windows_name ? { windows_name: p.windows_name } : {}),
            handles_kinds: p.handles_kinds.filter(Boolean),
          })),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "print-agents", agent.id] });
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  const updateRow = (idx: number, patch: Partial<PrintAgentPrinter>) =>
    setRows((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const addRow = () =>
    setRows((prev) => [...prev, { name: "", handles_kinds: [] }]);

  const removeRow = (idx: number) =>
    setRows((prev) => prev.filter((_, i) => i !== idx));

  return (
    <section className="card p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base">{t("printAgentDetail.printers")}</h3>
        <button className="btn" onClick={addRow}>
          {t("printAgents.addPrinter")}
        </button>
      </div>
      <p className="text-text-muted text-xs">
        {t("printAgentDetail.printersHint1")} <code className="font-mono">POST /register</code> {t("printAgentDetail.printersHint2")} <code className="font-mono">agent.toml</code>{t("printAgentDetail.printersHint3")}
      </p>

      {rows.length === 0 ? (
        <p className="text-text-muted text-sm">{t("printAgentDetail.noPrinters")}</p>
      ) : (
        <div className="space-y-2">
          {rows.map((p, idx) => (
            <div key={p.id ?? idx} className="card p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2 items-start">
                <input
                  type="text"
                  value={p.name}
                  onChange={(e) => updateRow(idx, { name: e.target.value })}
                  placeholder="laser-bw"
                  className="px-3 py-2 rounded-md bg-surface border border-border text-sm"
                />
                <input
                  type="text"
                  value={p.handles_kinds.join(", ")}
                  onChange={(e) =>
                    updateRow(idx, {
                      handles_kinds: e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  placeholder="passport, packing-list, op-token-A4"
                  className="px-3 py-2 rounded-md bg-surface border border-border text-xs font-mono"
                />
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="btn"
                  aria-label={t("common.delete")}
                  title={t("common.delete")}
                >
                  ✕
                </button>
              </div>
              <p className="text-text-muted text-xs flex items-center gap-2 ml-1">
                <span className="font-mono uppercase tracking-wider opacity-70">windows:</span>
                {p.windows_name ? (
                  <code className="font-mono">{p.windows_name}</code>
                ) : (
                  <span className="italic opacity-70">
                    {t("printAgentDetail.windowsNotProvided")}{" "}
                    <code className="font-mono">/register</code>
                  </span>
                )}
              </p>
            </div>
          ))}
        </div>
      )}

      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          className="btn btn-primary"
          onClick={() => {
            setErr(null);
            save.mutate();
          }}
          disabled={save.isPending}
        >
          {save.isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </section>
  );
}

// =============================================================
// Token-once modal (reused on rotate-token success)
// =============================================================

function TokenModal({
  token,
  agentName,
  onClose,
}: {
  token: string;
  agentName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* fallthrough — operator can copy manually */
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="card max-w-2xl w-full p-6 space-y-4">
        <header>
          <h2 className="text-lg">{t("printAgentDetail.newTokenTitle", { name: agentName })}</h2>
          <p className="text-text-muted text-sm mt-1">
            {t("printAgentDetail.newTokenWarning1")} <strong>{t("printAgents.tokenWarningBold")}</strong>{t("printAgentDetail.newTokenWarning2")}
          </p>
        </header>
        <div className="flex items-stretch gap-2">
          <code className="flex-1 px-3 py-2 rounded-md bg-surface border border-border text-xs font-mono break-all">
            {token}
          </code>
          <button type="button" className="btn" onClick={copy}>
            {copied ? t("printAgents.copied") : t("printAgents.copy")}
          </button>
        </div>
        <p className="text-text-muted text-xs">
          {t("printAgentDetail.pasteInto")} <code className="font-mono">agent.toml</code> {t("printAgentDetail.pasteField")}{" "}
          <code className="font-mono">auth_token</code> {t("printAgentDetail.pasteRestart")}
        </p>
        <div className="flex items-center justify-end gap-2 pt-2">
          <button className="btn btn-primary" onClick={onClose}>
            {t("printAgents.savedClose")}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Small bits
// =============================================================

function StatusCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "ok" | "warn" | "muted";
}) {
  return (
    <div className="card p-4">
      <p className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</p>
      <p
        className={
          "text-lg mt-1 " +
          (tone === "ok"
            ? "text-state-ok"
            : tone === "warn"
              ? "text-state-warn"
              : "")
        }
      >
        {value}
      </p>
    </div>
  );
}

function isOnline(a: PrintAgentRead): boolean {
  if (!a.is_active || !a.last_seen_at) return false;
  const dt = new Date(a.last_seen_at).getTime();
  return Number.isFinite(dt) && Date.now() - dt < ONLINE_FRESH_SECONDS * 1000;
}

function formatRelative(iso: string, t: TFunction): string {
  const dt = new Date(iso).getTime();
  if (!Number.isFinite(dt)) return iso;
  const diff = Math.max(0, Math.round((Date.now() - dt) / 1000));
  if (diff < 60) return t("printAgents.secAgo", { n: diff });
  if (diff < 3600) return t("printAgents.minAgo", { n: Math.round(diff / 60) });
  if (diff < 86_400) return t("printAgents.hourAgo", { n: Math.round(diff / 3600) });
  return new Date(iso).toLocaleString();
}
